import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { userManagementApi } from '../api/userManagement';
import * as api from '../api/contentStudioV2';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SpiderRadar from '../pages/contentV2/components/SpiderRadar';

// ============================================================
// STEP DETAIL MODAL COMPONENT
// ============================================================
const StepDetailModal = ({ isOpen, onClose, stepNumber, stepTitle, children }) => {
  if (!isOpen) return null;
  
  // Get step-specific colors
  const getStepColors = () => {
    switch(stepNumber) {
      case '6': return { bg: 'from-purple-600 to-purple-800', accent: '📋' };
      case '7': return { bg: 'from-blue-600 to-blue-800', accent: '🧠' };
      case '9': return { bg: 'from-orange-500 to-red-600', accent: '📝' };
      case '10': return { bg: 'from-green-600 to-emerald-700', accent: '🎬' };
      default: return { bg: 'from-gray-600 to-gray-800', accent: '📄' };
    }
  };
  
  const colors = getStepColors();
  
  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal - Full height on mobile, leaves space for browser bar and nav */}
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom duration-200"
        style={{
          height: 'calc(100vh - 80px)', // Leave space for browser bar
          maxHeight: 'calc(100vh - 80px)',
          marginBottom: '64px', // Space for bottom navigation
        }}
      >
        {/* Header - Fixed */}
        <div className={`flex-shrink-0 bg-gradient-to-r ${colors.bg} text-white p-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-xl shadow-lg">
                {colors.accent}
              </div>
              <div>
                <div className="text-xs font-medium text-white text-opacity-80">Step {stepNumber}</div>
                <h3 className="font-black text-lg">{stepTitle}</h3>
                <p className="text-xs text-white text-opacity-70">Executor View</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all text-lg font-bold"
            >
              ✕
            </button>
          </div>
        </div>
        
        {/* Content - Scrollable with extra bottom padding for mobile safe area */}
        <div className="flex-1 overflow-y-auto p-4 pb-32">
          {children}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// CHIP COMPONENT (Reusable)
// ============================================================
const Chip = ({ text, bg = '#E8F4FF', color = '#0e67a2' }) => (
  <span className="px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap" style={{ background: bg, color }}>
    {text}
  </span>
);

// ============================================================
// STEP 6 MODAL CONTENT - Review & Prime (Matches Executor View)
// ============================================================
const Step6Content = ({ submission }) => {
  const platformContext = submission?.platform_context || {};
  const objective = submission?.objective_type || 'Sales';
  
  // Correct field names from database
  const audienceDemographics = submission?.audience_demographics || submission?.audience || {};
  const audienceLanguages = submission?.audience_languages || {};
  const knowledgeSpider = submission?.knowledge_spider || {};
  const weights = submission?.knowledge_weights || submission?.weights || {};
  const evidenceMode = submission?.evidence_mode || 'Hybrid';
  
  // Extract products/services from knowledge_spider
  const products = knowledgeSpider?.products || [];
  const services = knowledgeSpider?.services || [];
  const values = knowledgeSpider?.values || submission?.selected_values || [];
  
  const soaPrime = submission?.soa_prime || {};
  const narrativeText = soaPrime.narrative || '';
  const benefitEmphasis = soaPrime.benefit_emphasis || [];
  const creativeIntent = submission?.creative_intent || {};

  return (
    <div className="space-y-3">
      {/* Platform & Duration */}
      <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
        <div className="text-xs font-bold text-gray-600 mb-2">📱 Platform & Duration</div>
        <div className="flex flex-wrap gap-2">
          {(platformContext.channels || []).map((platform, i) => (
            <Chip key={`platform-${platform}`} text={platform} bg="#E8F4FF" color="#0e67a2" />
          ))}
          {platformContext.normalized_seconds && (
            <Chip text={`${platformContext.normalized_seconds}s`} bg="#FFF4E6" color="#F7941E" />
          )}
        </div>
      </div>

      {/* Objective */}
      <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
        <div className="text-xs font-bold text-gray-600 mb-2">🎯 Objective</div>
        <Chip 
          text={objective} 
          bg={objective === 'Sales' ? '#E8F5E9' : '#FFF4E6'} 
          color={objective === 'Sales' ? '#2E7D32' : '#F7941E'} 
        />
      </div>

      {/* Audience Demographics */}
      <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
        <div className="text-xs font-bold text-gray-600 mb-2">👥 Audience</div>
        <div className="space-y-2">
          {/* Countries & Age */}
          <div className="flex flex-wrap gap-1">
            {audienceDemographics.countries?.map((c, i) => <Chip key={`country-${c}`} text={`🇲🇾 ${c}`} />)}
            {audienceDemographics.age_ranges?.map((a, i) => <Chip key={`age-${a}`} text={a} bg="#E3F2FD" color="#0e67a2" />)}
            {audienceDemographics.gender && <Chip text={audienceDemographics.gender} bg="#F3E5F5" color="#7B1FA2" />}
          </div>
          
          {/* Location Types & States */}
          {(audienceDemographics.location_types?.length > 0 || audienceDemographics.states?.length > 0) && (
            <div className="flex flex-wrap gap-1">
              {audienceDemographics.location_types?.map((l, i) => <Chip key={`loc-${l}`} text={l} bg="#FFF4E6" color="#F7941E" />)}
              {audienceDemographics.states?.slice(0, 3).map((s, i) => <Chip key={`state-${s}`} text={s} bg="#E8F5E9" color="#2E7D32" />)}
              {audienceDemographics.states?.length > 3 && <Chip text={`+${audienceDemographics.states.length - 3} more`} bg="#E8F5E9" color="#2E7D32" />}
            </div>
          )}
          
          {/* Interests */}
          {audienceDemographics.interests?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {audienceDemographics.interests.map((interest, i) => (
                <Chip key={`interest-${interest}`} text={`💡 ${interest}`} bg="#E8F5E9" color="#2E7D32" />
              ))}
            </div>
          )}
          
          {/* Languages & Slang */}
          {(audienceLanguages.languages?.length > 0 || audienceDemographics.slang?.length > 0) && (
            <div className="flex flex-wrap gap-1">
              {audienceLanguages.languages?.map((l, i) => <Chip key={`lang-${l}`} text={l} bg="#E8F4FF" color="#0e67a2" />)}
              {audienceLanguages.primary_language && <Chip text={`Primary: ${audienceLanguages.primary_language}`} bg="#E8F4FF" color="#0e67a2" />}
              {audienceDemographics.slang?.slice(0, 2).map((s, i) => <Chip key={`slang-${s}`} text={s} bg="#FFEBEE" color="#C62828" />)}
              {audienceLanguages.tone_preference && <Chip text={audienceLanguages.tone_preference} bg="#F3E5F5" color="#7B1FA2" />}
            </div>
          )}
        </div>
      </div>

      {/* Knowledge Packs */}
      <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
        <div className="text-xs font-bold text-gray-600 mb-2">📦 Knowledge Packs</div>
        <div className="space-y-2">
          {/* Products */}
          {products.length > 0 && (
            <div>
              <div className="flex flex-wrap gap-1 mb-2">
                <Chip text={`${products.length} Product${products.length > 1 ? 's' : ''}`} bg="#E8F5E9" color="#2E7D32" />
              </div>
              <div className="space-y-1">
                {products.map((p, i) => {
                  // Try multiple paths to get product name
                  const productName = 
                    p.metadata?.title?.en || 
                    p.metadata?.title?.bm ||
                    p.metadata?.title ||
                    p.content?.title?.en ||
                    p.content?.title ||
                    p.title?.en ||
                    p.title ||
                    p.name ||
                    p.pack_id ||
                    `Product ${i+1}`;
                  
                  return (
                    <div key={`product-${i}-${p?.pack_id || i}`} className="flex items-center gap-2 bg-white border border-green-200 rounded-lg px-3 py-2">
                      <span className="text-green-600">✓</span>
                      <span className="text-sm text-gray-700 font-medium">{productName}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Services */}
          {services.length > 0 && (
            <div className="flex flex-wrap gap-1">
              <Chip text={`${services.length} Service${services.length > 1 ? 's' : ''}`} bg="#E3F2FD" color="#0e67a2" />
            </div>
          )}
          
          {/* Values */}
          {values.length > 0 && (
            <div className="flex flex-wrap gap-1">
              <Chip text={`${values.length} Values`} bg="#F3E5F5" color="#7B1FA2" />
            </div>
          )}
          
          {/* Related Knowledge */}
          {(knowledgeSpider.related_promotions?.length > 0 || knowledgeSpider.related_campaigns?.length > 0) && (
            <div className="flex flex-wrap gap-1 mt-1">
              {knowledgeSpider.related_promotions?.length > 0 && (
                <Chip text={`${knowledgeSpider.related_promotions.length} Promotions`} bg="#FFF4E6" color="#F7941E" />
              )}
              {knowledgeSpider.related_campaigns?.length > 0 && (
                <Chip text={`${knowledgeSpider.related_campaigns.length} Campaigns`} bg="#FFF4E6" color="#F7941E" />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Spider Web Weights */}
      {weights && Object.keys(weights).length > 0 && (
        <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
          <div className="text-xs font-bold text-gray-600 mb-2">🕸️ Knowledge Weights</div>
          <div className="flex justify-center">
            <SpiderRadar 
              values={weights} 
              size={220}
            />
          </div>
        </div>
      )}

      {/* Evidence Mode */}
      <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
        <div className="text-xs font-bold text-gray-600 mb-2">📊 Evidence Mode</div>
        <Chip 
          text={evidenceMode} 
          bg={evidenceMode === 'Scientific' ? '#E3F2FD' : evidenceMode === 'Emotional' ? '#FCE4EC' : '#FFF4E6'} 
          color={evidenceMode === 'Scientific' ? '#0e67a2' : evidenceMode === 'Emotional' ? '#C2185B' : '#F57C00'} 
        />
      </div>

      {/* Context Narrative (Generated) */}
      {narrativeText && (
        <div className="bg-green-50 rounded-xl p-3 border border-green-400">
          <div className="text-xs font-bold text-green-700 mb-2">💡 Context Narrative</div>
          <p className="text-sm text-green-800 leading-relaxed">{narrativeText}</p>
        </div>
      )}

      {/* Applied Benefit Emphasis */}
      {benefitEmphasis && benefitEmphasis.length > 0 && (
        <div className="bg-yellow-50 rounded-xl p-3 border border-yellow-400">
          <div className="text-xs font-bold text-yellow-700 mb-2">🎯 Applied Benefit Emphasis</div>
          <div className="text-xs text-gray-600 mb-2">Pain point suggestions were focused based on these benefit weights:</div>
          <div className="space-y-2">
            {benefitEmphasis
              .filter(b => b.emphasis >= 30)
              .sort((a, b) => b.emphasis - a.emphasis)
              .slice(0, 5)
              .map((benefit, idx) => {
                const getBg = (val) => val >= 70 ? '#FFEBEE' : val >= 50 ? '#FFF4E6' : '#F5F5F5';
                const getColor = (val) => val >= 70 ? '#C62828' : val >= 50 ? '#F57C00' : '#666';
                return (
                  <div key={`benefit-${idx}-${benefit?.emphasis || idx}`} className="flex items-start gap-2 p-2 rounded-lg text-xs" style={{ background: getBg(benefit.emphasis), color: getColor(benefit.emphasis) }}>
                    <span className="font-bold min-w-[32px] text-center bg-white rounded px-1 py-0.5">{benefit.emphasis}</span>
                    <span className="flex-1 leading-relaxed">{benefit.benefit_text}</span>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Creative Intent */}
      {creativeIntent && creativeIntent.style && (
        <div className="bg-pink-50 rounded-xl p-3 border border-pink-200">
          <div className="text-xs font-bold text-pink-700 mb-2">✨ Creative Intent</div>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-white rounded-lg p-2 text-center">
              <div className="text-xs text-gray-500">Style</div>
              <div className="font-bold text-sm">{creativeIntent.style}</div>
            </div>
            <div className="bg-white rounded-lg p-2 text-center">
              <div className="text-xs text-gray-500">Visual Mode</div>
              <div className="font-bold text-sm">{creativeIntent.visual_mode || 'N/A'}</div>
            </div>
          </div>
          {creativeIntent.emotions?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {creativeIntent.emotions.map((e, i) => (
                <Chip key={`evidence-${e}`} text={e} bg="#FCE4EC" color="#C2185B" />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================
// STEP 7 MODAL CONTENT - SOA Drill
// ============================================================
const Step7Content = ({ soaSummary, soaV2, soaPrime, soaAnswersV2 }) => {
  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Get questions from blueprint (soa_prime)
  const blueprint = soaPrime?.blueprint || [];
  // Get answers from soa_v2
  const answers = soaV2?.answers || [];
  // Get per_question data from summary
  const perQuestion = soaSummary?.per_question || [];
  // Get full answer history from soa_answers_v2
  const answersHistory = soaAnswersV2 || {};
  
  // AI Rescue penalty calculation
  const aiRescueCount = soaSummary?.ai_rescue_count || 0;
  const bpPenaltyApplied = soaSummary?.bp_penalty_applied || false;
  const baseBP = soaSummary?.passed ? 15 : 5;
  // Formula: BP = base_bp * (1 - (0.3 * ai_rescue_count / 5))
  const penaltyFactor = aiRescueCount > 0 ? (0.3 * aiRescueCount / 5) : 0;
  const penaltyPercentage = Math.round(penaltyFactor * 100);
  const penaltyBP = Math.round(baseBP * penaltyFactor);

  return (
    <div className="space-y-4 pb-8">
      {/* Overall Score */}
      <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl p-4 text-white text-center">
        <div className="text-5xl font-black mb-2">
          {soaSummary?.avg_score ? soaSummary.avg_score.toFixed(1) : 'N/A'}
          <span className="text-2xl font-normal">/10</span>
        </div>
        <div className="text-white text-opacity-80">Average SOA Score</div>
        <div className="flex justify-center gap-2 mt-2 flex-wrap">
          {soaSummary?.passed && (
            <Badge className="bg-green-400 text-white">✅ Passed</Badge>
          )}
          {soaSummary?.proceeded_anyway && (
            <Badge className="bg-orange-400 text-white">⚠️ Proceeded Anyway</Badge>
          )}
          {soaSummary?.bp_awarded > 0 && (
            <Badge className="bg-yellow-400 text-gray-800">🧠 +{soaSummary.bp_awarded} BP</Badge>
          )}
        </div>
      </div>
      
      {/* AI Rescue Penalty Warning - Show if AI Rescue was used */}
      {aiRescueCount > 0 && (
        <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-4 border-2 border-red-200">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-2xl">🆘</span>
            <span className="font-bold text-red-700 text-lg">AI Rescue Penalty Applied</span>
          </div>
          
          <div className="bg-white rounded-lg p-3 space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">AI Rescue Used:</span>
              <span className="font-bold text-red-600">{aiRescueCount} question{aiRescueCount > 1 ? 's' : ''}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Penalty Rate:</span>
              <span className="font-bold text-red-600">-{penaltyPercentage}% BP</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Base BP Earned:</span>
              <span className="font-bold text-gray-700">{baseBP} BP</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Penalty Deduction:</span>
              <span className="font-bold text-red-600">-{penaltyBP} BP</span>
            </div>
            <div className="border-t pt-2 flex justify-between items-center">
              <span className="font-bold text-gray-700">Final BP Awarded:</span>
              <span className="font-black text-lg text-green-600">{soaSummary?.bp_awarded || (baseBP - penaltyBP)} BP</span>
            </div>
          </div>
          
          <div className="mt-3 text-xs text-red-600 bg-red-100 rounded-lg p-2">
            💡 <strong>Coaching Note:</strong> Executor relied on AI assistance for {aiRescueCount} question{aiRescueCount > 1 ? 's' : ''}. 
            Consider reviewing their understanding of these areas.
          </div>
        </div>
      )}
      
      {/* Questions & Answers */}
      <div className="space-y-3">
        <h4 className="font-bold text-gray-800 flex items-center gap-2">
          <span>📊</span> Question & Answer Breakdown
        </h4>
        
        {/* Loop through questions using perQuestion or blueprint */}
        {(perQuestion.length > 0 ? perQuestion : blueprint).map((item, idx) => {
          // Get data from different sources
          const questionData = perQuestion[idx] || {};
          const answerData = answers[idx] || {};
          const blueprintQ = blueprint[idx] || {};
          
          // Get answer history for this question
          const questionAnswerHistory = answersHistory[String(idx)] || [];
          
          const score = questionData.best_score || answerData.best_score || answerData.score || 0;
          const bestAnswer = questionData.best_answer || answerData.best_answer || answerData.answer || '';
          const attempts = questionData.attempts || answerData.attempts || questionAnswerHistory.length || 1;
          const aiRescue = questionData.ai_rescue_accepted || answerData.ai_rescue_accepted || false;
          const questionText = blueprintQ.q || blueprintQ.question || `Question ${idx + 1}`;
          const category = blueprintQ.category || 'General';
          
          // Find the AI rescue answer from history (it has ai_rescue_accepted = true)
          const aiRescueAnswerEntry = questionAnswerHistory.find(a => a.ai_rescue_accepted);
          const aiRescueAnswer = aiRescueAnswerEntry?.ai_rescue_answer || '';
          
          // Find executor's original answer (the answer before AI rescue was accepted, or their actual input)
          // The original answer is stored in the same entry that has ai_rescue_accepted
          const executorOriginalAnswer = aiRescueAnswerEntry?.answer || questionAnswerHistory[0]?.answer || '';
          const originalScore = aiRescueAnswerEntry?.original_score || questionAnswerHistory[0]?.score || 0;
          
          return (
            <div key={`answer-${idx}`} className={`rounded-xl p-3 border ${aiRescue ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-100'}`}>
              {/* Question Header */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={aiRescue ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'}>Q{idx + 1}</Badge>
                  <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">{category}</span>
                  {aiRescue && (
                    <span className="text-xs bg-red-200 text-red-700 px-2 py-0.5 rounded font-bold">🆘 AI Rescue (-6% BP)</span>
                  )}
                </div>
                <div className={`text-lg font-black ${getScoreColor(score)}`}>
                  {score?.toFixed ? score.toFixed(1) : score}/10
                </div>
              </div>
              
              {/* Question Text */}
              <div className={`rounded-lg p-2 mb-2 border ${aiRescue ? 'bg-red-100 border-red-200' : 'bg-blue-50 border-blue-100'}`}>
                <div className={`text-xs font-bold mb-1 ${aiRescue ? 'text-red-600' : 'text-blue-600'}`}>❓ Question:</div>
                <p className={`text-sm ${aiRescue ? 'text-red-800' : 'text-blue-800'}`}>{questionText}</p>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div 
                  className={`h-2 rounded-full transition-all ${
                    score >= 8 ? 'bg-green-500' :
                    score >= 6 ? 'bg-yellow-500' :
                    'bg-red-500'
                  }`}
                  style={{ width: `${(score || 0) * 10}%` }}
                />
              </div>
              
              {/* For AI Rescue questions, show both original and AI answer */}
              {aiRescue ? (
                <div className="space-y-2">
                  {/* Executor's Original Answer (before AI Rescue) */}
                  {executorOriginalAnswer && (
                    <div className="bg-orange-50 rounded-lg p-2 border border-orange-200">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs font-bold text-orange-600">❌ Executor&apos;s Original Answer:</div>
                        {originalScore > 0 && (
                          <span className="text-xs text-orange-500 font-bold">Score: {originalScore.toFixed ? originalScore.toFixed(1) : originalScore}/10</span>
                        )}
                      </div>
                      <p className="text-sm text-orange-800 leading-relaxed">{executorOriginalAnswer}</p>
                    </div>
                  )}
                  
                  {/* AI Suggested Answer */}
                  <div className="bg-purple-50 rounded-lg p-2 border border-purple-200">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="text-xs font-bold text-purple-600">🤖 AI Suggested Answer:</div>
                      <span className="text-xs bg-purple-200 text-purple-700 px-1.5 py-0.5 rounded">Accepted</span>
                    </div>
                    {aiRescueAnswer ? (
                      <p className="text-sm text-purple-800 leading-relaxed">{aiRescueAnswer}</p>
                    ) : (
                      <p className="text-sm text-purple-600 italic">AI answer was not stored for this submission (legacy data)</p>
                    )}
                  </div>
                  
                  {/* Final Score Note */}
                  <div className="text-xs text-red-600 bg-red-100 rounded p-2">
                    ⚠️ Executor accepted AI assistance. Score adjusted to 8.2/10 with BP penalty applied.
                  </div>
                </div>
              ) : (
                /* Normal Answer (no AI Rescue) */
                bestAnswer && (
                  <div className="bg-white rounded-lg p-2 border border-gray-200">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs text-gray-500 font-bold">💬 Executor&apos;s Answer:</div>
                      {attempts > 1 && (
                        <span className="text-xs text-gray-400">{attempts} attempts</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">{bestAnswer}</p>
                  </div>
                )
              )}
            </div>
          );
        })}
      </div>
      
      {/* Coaching Insights */}
      {(soaSummary?.strongest_question || soaSummary?.weakest_question) && (
        <div className="space-y-2">
          <h4 className="font-bold text-gray-800 flex items-center gap-2">
            <span>💡</span> Coaching Insights
          </h4>
          <div className="grid grid-cols-2 gap-3">
            {soaSummary?.strongest_question && (
              <div className="bg-green-50 rounded-xl p-3 border border-green-100">
                <div className="text-xs text-green-600 font-bold mb-1">💪 Strength</div>
                <div className="text-lg font-bold text-green-700">
                  Q{soaSummary.strongest_question.q_num}
                </div>
                <div className="text-sm text-green-600">
                  {soaSummary.strongest_question.score?.toFixed(1)}/10
                </div>
              </div>
            )}
            {soaSummary?.weakest_question && (
              <div className="bg-orange-50 rounded-xl p-3 border border-orange-100">
                <div className="text-xs text-orange-600 font-bold mb-1">📝 Focus Area</div>
                <div className="text-lg font-bold text-orange-700">
                  Q{soaSummary.weakest_question.q_num}
                </div>
                <div className="text-sm text-orange-600">
                  {soaSummary.weakest_question.score?.toFixed(1)}/10
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// STEP 9 MODAL CONTENT - Script Selection
// ============================================================
const Step9Content = ({ selectedScript, scriptVariants }) => {
  const [expandedVariants, setExpandedVariants] = React.useState({});
  
  const toggleVariant = (variantId) => {
    setExpandedVariants(prev => ({
      ...prev,
      [variantId]: !prev[variantId]
    }));
  };
  
  // Extract script text from various possible formats
  const getScriptText = (v) => {
    if (v.script_text) return v.script_text;
    if (v.sections && v.sections.length > 0) {
      return v.sections.map((s, i) => {
        const title = s.title || s.section_name || `Section ${i+1}`;
        const duration = s.duration_seconds || 0;
        const lines = s.lines?.join('\n') || s.content || s.text || '';
        return `[${title}] (${duration}s)\n${lines}`;
      }).join('\n\n');
    }
    return 'Script content not available';
  };

  return (
    <div className="space-y-4">
      {/* Selected Script Header */}
      <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-4 text-white">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs text-white text-opacity-80 mb-1">Selected Variant</div>
            <div className="text-2xl font-black">{selectedScript.variant_id || 'Variant 1'}</div>
          </div>
          <div className="text-right">
            <Badge className="bg-white bg-opacity-20 text-white mb-1">
              {selectedScript.style || 'N/A'}
            </Badge>
            <div className="text-xs text-white text-opacity-80">
              {selectedScript.focus || 'General Focus'}
            </div>
          </div>
        </div>
        <div className="text-sm text-white text-opacity-90">
          {scriptVariants.length} variants were generated
        </div>
      </div>
      
      {/* Script Content */}
      {selectedScript.sections && selectedScript.sections.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-bold text-gray-800 flex items-center gap-2">
            <span>📝</span> Selected Script
          </h4>
          {selectedScript.sections.map((section, i) => (
            <div key={`section-${i}-${section?.type || i}`} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <Badge className={
                  section.title?.toLowerCase().includes('hook') ? 'bg-red-500 text-white' :
                  section.title?.toLowerCase().includes('cta') ? 'bg-green-500 text-white' :
                  'bg-gray-500 text-white'
                }>
                  {section.title || `Section ${i + 1}`}
                </Badge>
                <span className="text-xs text-gray-500">
                  {section.duration_seconds || 0}s
                </span>
              </div>
              <div className="bg-white rounded-lg p-3 border border-gray-100">
                {section.lines && section.lines.length > 0 ? (
                  <ul className="space-y-2">
                    {section.lines.map((line, j) => (
                      <li key={j} className="text-sm text-gray-700 flex items-start gap-2">
                        <span className="text-orange-500 mt-1">▸</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-700">{section.content || section.text || 'No content'}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Raw Script Text (fallback) */}
      {!selectedScript.sections && selectedScript.script_text && (
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
          <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
            <span>📄</span> Full Script
          </h4>
          <div className="bg-white rounded-lg p-4 border border-gray-100 whitespace-pre-wrap text-sm text-gray-700 max-h-64 overflow-y-auto">
            {selectedScript.script_text}
          </div>
        </div>
      )}
      
      {/* Other Variants - Collapsible */}
      {scriptVariants.length > 1 && (
        <div className="space-y-3">
          <h4 className="font-bold text-gray-800 flex items-center gap-2">
            <span>📋</span> Other Variants
            <span className="text-xs font-normal text-gray-500">({scriptVariants.length - 1} not selected)</span>
          </h4>
          <p className="text-xs text-gray-500">Tap to expand and review alternatives for feedback</p>
          
          {scriptVariants.filter(v => v.variant_id !== selectedScript.variant_id).map((variant, i) => {
            const variantKey = variant.variant_id || `variant_${i}`;
            const isExpanded = expandedVariants[variantKey];
            
            return (
              <div key={`variant-${i}-${variant?.variant_id || i}`} className="bg-blue-50 rounded-xl border border-blue-200 overflow-hidden">
                {/* Collapsible Header - Always visible */}
                <button
                  onClick={() => toggleVariant(variantKey)}
                  className="w-full p-3 flex items-center justify-between hover:bg-blue-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-500 text-white">
                      {variant.variant_id || `Variant ${String.fromCharCode(66 + i)}`}
                    </Badge>
                    <span className="text-sm text-blue-700 font-medium">
                      {variant.style || 'Alternative Style'}
                    </span>
                    {variant.focus && (
                      <span className="text-xs text-gray-500">• {variant.focus}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-blue-600">
                      {isExpanded ? 'Hide' : 'View Script'}
                    </span>
                    <span className={`text-blue-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                      ▼
                    </span>
                  </div>
                </button>
                
                {/* Expandable Content */}
                {isExpanded && (
                  <div className="p-3 pt-0 border-t border-blue-200 bg-white">
                    {variant.sections && variant.sections.length > 0 ? (
                      <div className="space-y-3 mt-3">
                        {variant.sections.map((section, j) => (
                          <div key={j}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                                section.title?.toLowerCase().includes('hook') ? 'bg-red-100 text-red-600' :
                                section.title?.toLowerCase().includes('cta') ? 'bg-green-100 text-green-600' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {section.title || `Section ${j + 1}`}
                              </span>
                              <span className="text-xs text-gray-400">{section.duration_seconds || 0}s</span>
                            </div>
                            <div className="text-sm text-gray-700 pl-2 border-l-2 border-blue-200">
                              {section.lines && section.lines.length > 0 ? (
                                section.lines.map((line, k) => (
                                  <p key={k} className="mb-1">{line}</p>
                                ))
                              ) : (
                                <p>{section.content || section.text || 'No content'}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-700 whitespace-pre-wrap mt-3">
                        {getScriptText(variant)}
                      </div>
                    )}
                    
                    {/* Recommendation hint */}
                    <div className="mt-3 text-xs text-blue-600 bg-blue-50 rounded p-2 border border-blue-100">
                      💡 Prefer this variant? Mention it in your approval comments to guide the executor.
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================================
// STEP 10 MODAL CONTENT - Storyboard
// ============================================================
const Step10Content = ({ submissionScores, storyboardBeats }) => (
  <div className="space-y-4">
    {/* AI Quality Score Header */}
    {submissionScores && (
      <div className={`rounded-xl p-4 text-white ${
        submissionScores.overall_percentage >= 80 ? 'bg-gradient-to-br from-green-500 to-emerald-600' :
        submissionScores.overall_percentage >= 60 ? 'bg-gradient-to-br from-yellow-500 to-orange-500' :
        'bg-gradient-to-br from-red-500 to-pink-600'
      }`}>
        <div className="text-center mb-3">
          <div className="text-5xl font-black">
            {submissionScores.overall_percentage || 0}%
          </div>
          <div className="text-white text-opacity-80">AI Quality Score</div>
        </div>
        {submissionScores.overall_feedback && (
          <div className="bg-white bg-opacity-20 rounded-lg p-3 text-sm">
            {submissionScores.overall_feedback}
          </div>
        )}
      </div>
    )}
    
    {/* Score Breakdown */}
    {submissionScores && (
      <div className="grid grid-cols-2 gap-3">
        {/* Script Scores */}
        {submissionScores.script_scores && (
          <div className="bg-orange-50 rounded-xl p-3 border border-orange-100">
            <div className="text-xs text-orange-600 font-bold mb-2">📝 Script</div>
            <div className="text-2xl font-black text-orange-700 mb-2">
              {submissionScores.script_average || 0}/10
            </div>
            <div className="space-y-1">
              {Object.entries(submissionScores.script_scores).slice(0, 3).map(([key, val]) => (
                <div key={key} className="flex justify-between text-xs">
                  <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}</span>
                  <span className="font-bold">{val.score}/10</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Storyboard Scores */}
        {submissionScores.storyboard_scores && (
          <div className="bg-green-50 rounded-xl p-3 border border-green-100">
            <div className="text-xs text-green-600 font-bold mb-2">🎬 Storyboard</div>
            <div className="text-2xl font-black text-green-700 mb-2">
              {submissionScores.storyboard_average || 0}/10
            </div>
            <div className="space-y-1">
              {Object.entries(submissionScores.storyboard_scores).slice(0, 3).map(([key, val]) => (
                <div key={key} className="flex justify-between text-xs">
                  <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}</span>
                  <span className="font-bold">{val.score}/10</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )}
    
    {/* Strengths & Improvements */}
    {submissionScores && (
      <div className="grid grid-cols-2 gap-3">
        {submissionScores.strengths?.length > 0 && (
          <div className="bg-green-50 rounded-xl p-3 border border-green-100">
            <div className="text-xs text-green-600 font-bold mb-2">✅ Strengths</div>
            <ul className="space-y-1">
              {submissionScores.strengths.map((s, i) => (
                <li key={`strength-${i}`} className="text-xs text-green-700 flex items-start gap-1">
                  <span>•</span><span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {submissionScores.improvements?.length > 0 && (
          <div className="bg-orange-50 rounded-xl p-3 border border-orange-100">
            <div className="text-xs text-orange-600 font-bold mb-2">📝 Improve</div>
            <ul className="space-y-1">
              {submissionScores.improvements.map((s, i) => (
                <li key={`improve-${i}`} className="text-xs text-orange-700 flex items-start gap-1">
                  <span>•</span><span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )}
    
    {/* Storyboard Beats */}
    {storyboardBeats && storyboardBeats.length > 0 && (
      <div className="space-y-3 pb-8">
        <h4 className="font-bold text-gray-800 flex items-center gap-2">
          <span>🎬</span> Storyboard Beats ({storyboardBeats.length})
        </h4>
        <div className="space-y-3">
          {storyboardBeats.map((beat, index) => (
            <div key={`storybeat-${index}`} className="bg-gray-50 rounded-xl p-3 border-l-4 border-emzi-blue">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge className="bg-emzi-blue text-white">
                  Beat {beat.beat_number || index + 1}
                </Badge>
                <Badge variant="outline" className={
                  beat.scene_type === 'Hook' ? 'border-red-500 text-red-500' :
                  beat.scene_type === 'CTA' ? 'border-green-500 text-green-500' :
                  'border-gray-500 text-gray-500'
                }>
                  {beat.scene_type}
                </Badge>
                <span className="text-xs text-gray-500">{beat.timing} • {beat.duration_seconds}s</span>
              </div>
              <p className="text-sm text-gray-700 mb-2">{beat.description}</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-blue-50 rounded p-2">
                  <span className="font-bold text-blue-600">👁️ Visual:</span>
                  <span className="text-gray-600 ml-1">{beat.visual_notes}</span>
                </div>
                <div className="bg-purple-50 rounded p-2">
                  <span className="font-bold text-purple-600">🔊 Audio:</span>
                  <span className="text-gray-600 ml-1">{beat.audio_notes}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

// ============================================================
// MAIN COMPONENT
// ============================================================
const QueueReview = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { user, token, isSupervisor, isManagement, isAdmin } = useAuth();
  const t = useLang();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [comment, setComment] = useState('');
  const [activeModal, setActiveModal] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    journey: true
  });

  useEffect(() => {
    if (!isSupervisor() && !isManagement() && !isAdmin()) {
      navigate('/dashboard');
      return;
    }
    
    const loadSubmission = async () => {
      try {
        setLoading(true);
        const response = await api.getSessionData(token, sessionId);
        setSubmission(response);
      } catch (error) {
        console.error('Failed to load submission:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadSubmission();
  }, [sessionId, token, navigate, isSupervisor, isManagement, isAdmin]);

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      await userManagementApi.approvalAction({
        submission_id: submission.submission_id || sessionId,
        session_id: sessionId,
        action: 'approve',
        note: comment,
        source: 'content_studio'
      });
      alert('✅ Submission approved successfully!');
      navigate('/queue');
    } catch (error) {
      console.error('Approval failed:', error);
      alert('Failed to approve. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!comment.trim()) {
      alert('Please provide a reason for rejection.');
      return;
    }
    setActionLoading(true);
    try {
      await userManagementApi.approvalAction({
        submission_id: submission.submission_id || sessionId,
        session_id: sessionId,
        action: 'reject',
        note: comment,
        reason: comment,
        source: 'content_studio'
      });
      alert('❌ Submission rejected with feedback.');
      navigate('/queue');
    } catch (error) {
      console.error('Rejection failed:', error);
      alert('Failed to reject. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!comment.trim()) {
      alert('Please specify what changes are needed.');
      return;
    }
    setActionLoading(true);
    try {
      await userManagementApi.approvalAction({
        submission_id: submission.submission_id || sessionId,
        session_id: sessionId,
        action: 'request_changes',
        note: comment,
        source: 'content_studio'
      });
      alert('📝 Change request sent to executor.');
      navigate('/queue');
    } catch (error) {
      console.error('Request changes failed:', error);
      alert('Failed to send request. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading) {
    return (
      <div className="nexus-content bg-white flex items-center justify-center">
        <div className="text-gray-600 font-medium">Loading submission...</div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="nexus-content bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <h3 className="text-xl font-bold text-gray-800">Submission not found</h3>
          <Button onClick={() => navigate('/queue')} className="mt-4">Back to Queue</Button>
        </div>
      </div>
    );
  }

  // Extract data from submission
  const storyboardBeats = submission.storyboard_beats || [];
  const selectedScript = submission.selected_script || {};
  const platformContext = submission.platform_context || {};
  const selectedFormat = submission.selected_format || {};
  const soaSummary = submission.soa_summary_v2 || {};
  
  // Step 6 data is stored in soa_prime object
  const soaPrime = submission.soa_prime || {};
  const narrativeText = soaPrime.narrative || submission.narrative || '';
  
  const scriptVariants = submission.script_variants || [];
  const submissionScores = submission.submission_scores || null;

  // Calculate scores
  const step10Score = submissionScores?.overall_percentage || null;
  
  // Calculate overall journey score (weighted average)
  const calculateJourneyScore = () => {
    let totalWeight = 0;
    let totalScore = 0;
    
    if (soaSummary.avg_score) {
      totalScore += (soaSummary.avg_score / 10) * 30;
      totalWeight += 30;
    }
    
    if (submissionScores?.overall_percentage) {
      totalScore += (submissionScores.overall_percentage / 100) * 70;
      totalWeight += 70;
    }
    
    return totalWeight > 0 ? Math.round((totalScore / totalWeight) * 100) : null;
  };

  const journeyScore = calculateJourneyScore();

  return (
    <div className="nexus-content overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="px-5 py-4 bg-emzi-blue text-white flex-shrink-0 shadow-xl" style={{background: '#0e67a2'}}>
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Button 
              onClick={() => navigate('/queue')} 
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white hover:bg-opacity-20"
            >
              ← Back
            </Button>
            <div>
              <h1 className="text-xl font-black text-white">Review Submission</h1>
              <p className="text-sm text-white opacity-80">
                {submission.project_name || `${submission.objective_type || 'Storyboard'} - ${submission.submitted_at ? new Date(submission.submitted_at).toLocaleDateString('ms-MY', { day: '2-digit', month: 'short' }) : 'Pending'}`}
              </p>
              {/* Submitter Name */}
              <p className="text-xs text-yellow-300 font-semibold mt-1">
                👤 Submitted by: {submission.executor_name || submission.submitter_name || submission.user_email || submission.user_id?.slice(0, 8) || 'Unknown'}
              </p>
            </div>
          </div>
          <Badge className="bg-orange-500 text-white">
            {submission.submission_status || 'Pending'}
          </Badge>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto panel-scroll px-4 py-4 pb-28 space-y-4">
        
        {/* ============================================================ */}
        {/* EXECUTOR JOURNEY CARD */}
        {/* ============================================================ */}
        <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-blue-50">
          <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleSection('journey')}>
            <CardTitle className="text-lg font-bold text-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span>🎯 Executor Journey</span>
                {journeyScore !== null && (
                  <Badge className={journeyScore >= 80 ? 'bg-green-500' : journeyScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'}>
                    {journeyScore}% Overall
                  </Badge>
                )}
              </div>
              <span className="text-gray-400 text-sm">
                {expandedSections.journey ? '▼' : '▶'}
              </span>
            </CardTitle>
            <p className="text-xs text-gray-500 mt-1">
              Tap each step to view executor&apos;s detailed work
            </p>
          </CardHeader>
          
          {expandedSections.journey && (
            <CardContent className="space-y-4">
              {/* Journey Progress Bar */}
              <div className="relative pt-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-600">Journey Progress</span>
                  <span className="text-xs font-semibold text-gray-600">
                    {journeyScore !== null ? `${journeyScore}%` : 'N/A'}
                  </span>
                </div>
                <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                  <div
                    style={{ width: `${journeyScore || 0}%` }}
                    className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center transition-all duration-500 ${
                      journeyScore >= 80 ? 'bg-green-500' :
                      journeyScore >= 60 ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}
                  />
                </div>
              </div>

              {/* Step Cards Grid - Now Clickable */}
              <div className="grid grid-cols-2 gap-3">
                
                {/* Step 6: Review & Prime */}
                <div 
                  className="bg-white rounded-lg p-3 border border-gray-100 cursor-pointer hover:shadow-lg hover:border-purple-300 transition-all active:scale-95"
                  onClick={() => setActiveModal('step6')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-purple-600">📋 Step 6</span>
                    <Badge className="bg-purple-100 text-purple-700 text-xs">Review & Prime</Badge>
                  </div>
                  <div className="text-sm font-bold text-gray-800">
                    {narrativeText ? '✅ Complete' : '⏳ Pending'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Tap to view details →
                  </div>
                </div>

                {/* Step 7: SOA Drill */}
                <div 
                  className="bg-white rounded-lg p-3 border border-gray-100 cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all active:scale-95"
                  onClick={() => setActiveModal('step7')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-blue-600">🧠 Step 7</span>
                    <Badge className="bg-blue-100 text-blue-700 text-xs">SOA Drill</Badge>
                  </div>
                  <div className="text-sm font-bold text-gray-800">
                    {soaSummary.avg_score ? (
                      <span className={soaSummary.avg_score >= 8 ? 'text-green-600' : soaSummary.avg_score >= 6 ? 'text-yellow-600' : 'text-red-600'}>
                        {soaSummary.avg_score.toFixed(1)}/10 Avg
                      </span>
                    ) : '⏳ Pending'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Tap to view Q&A →
                  </div>
                </div>

                {/* Step 9: Script */}
                <div 
                  className="bg-white rounded-lg p-3 border border-gray-100 cursor-pointer hover:shadow-lg hover:border-orange-300 transition-all active:scale-95"
                  onClick={() => setActiveModal('step9')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-orange-600">📝 Step 9</span>
                    <Badge className="bg-orange-100 text-orange-700 text-xs">Script</Badge>
                  </div>
                  <div className="text-sm font-bold text-gray-800">
                    {selectedScript.variant_id ? '✅ Selected' : '⏳ Pending'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {selectedScript.style || 'Tap to view script →'}
                  </div>
                </div>

                {/* Step 10: Storyboard */}
                <div 
                  className="bg-white rounded-lg p-3 border border-gray-100 cursor-pointer hover:shadow-lg hover:border-green-300 transition-all active:scale-95"
                  onClick={() => setActiveModal('step10')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-green-600">🎬 Step 10</span>
                    <Badge className="bg-green-100 text-green-700 text-xs">Storyboard</Badge>
                  </div>
                  <div className="text-sm font-bold text-gray-800">
                    {step10Score !== null ? (
                      <span className={step10Score >= 80 ? 'text-green-600' : step10Score >= 60 ? 'text-yellow-600' : 'text-red-600'}>
                        {step10Score}%
                      </span>
                    ) : '⏳ Pending'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {storyboardBeats.length} beats • Tap to view →
                  </div>
                </div>
              </div>

              {/* Coaching Insights */}
              {(soaSummary.weakest_question || soaSummary.strongest_question) && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-3 border border-amber-200">
                  <div className="text-xs font-bold text-amber-700 mb-2">💡 Coaching Insights</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {soaSummary.strongest_question && (
                      <div className="bg-white rounded p-2">
                        <span className="text-green-600 font-bold">✅ Strength:</span>
                        <div className="text-gray-600 mt-1">Q{soaSummary.strongest_question.q_num}: {soaSummary.strongest_question.score?.toFixed(1)}/10</div>
                      </div>
                    )}
                    {soaSummary.weakest_question && (
                      <div className="bg-white rounded p-2">
                        <span className="text-orange-600 font-bold">📝 Focus Area:</span>
                        <div className="text-gray-600 mt-1">Q{soaSummary.weakest_question.q_num}: {soaSummary.weakest_question.score?.toFixed(1)}/10</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Summary Card */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold text-gray-800">📋 Submission Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Objective:</span>
                <div className="font-bold text-gray-800">{submission.objective_type || 'N/A'}</div>
              </div>
              <div>
                <span className="text-gray-500">Platform:</span>
                <div className="font-bold text-gray-800">{platformContext.channels?.join(', ') || 'N/A'}</div>
              </div>
              <div>
                <span className="text-gray-500">Duration:</span>
                <div className="font-bold text-gray-800">{platformContext.normalized_seconds || 60}s</div>
              </div>
              <div>
                <span className="text-gray-500">Format:</span>
                <div className="font-bold text-gray-800">{selectedFormat.format_name || 'N/A'}</div>
              </div>
              <div>
                <span className="text-gray-500">Executor:</span>
                <div className="font-bold text-gray-800">{submission.executor_name || submission.user_id?.slice(0, 8) || 'N/A'}</div>
              </div>
              <div>
                <span className="text-gray-500">Submitted:</span>
                <div className="font-bold text-gray-800">
                  {submission.submitted_at ? new Date(submission.submitted_at).toLocaleString() : 'N/A'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Decision Card - Fixed at bottom with nav spacing */}
        <Card className="border-0 shadow-lg bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold text-gray-800">🎯 Your Decision</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add feedback or reason for decision (required for rejection/changes)..."
              className="w-full p-3 border rounded-lg resize-none h-20 text-sm"
            />
            <div className="flex gap-2">
              <Button
                onClick={handleApprove}
                disabled={actionLoading}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-2 text-sm"
              >
                ✅ Approve
              </Button>
              <Button
                onClick={handleRequestChanges}
                disabled={actionLoading}
                variant="outline"
                className="flex-1 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white font-bold py-2 text-sm"
              >
                📝 Changes
              </Button>
              <Button
                onClick={handleReject}
                disabled={actionLoading}
                variant="outline"
                className="flex-1 border-red-500 text-red-500 hover:bg-red-500 hover:text-white font-bold py-2 text-sm"
              >
                ❌ Reject
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Bottom spacing for navigation */}
        <div className="h-20"></div>
      </div>

      {/* ============================================================ */}
      {/* STEP DETAIL MODALS */}
      {/* ============================================================ */}
      <StepDetailModal 
        isOpen={activeModal === 'step6'} 
        onClose={() => setActiveModal(null)}
        stepNumber="6"
        stepTitle="Review & Prime"
      >
        <Step6Content submission={submission} />
      </StepDetailModal>

      <StepDetailModal 
        isOpen={activeModal === 'step7'} 
        onClose={() => setActiveModal(null)}
        stepNumber="7"
        stepTitle="SOA Drill"
      >
        <Step7Content 
          soaSummary={soaSummary} 
          soaV2={submission.soa_v2} 
          soaPrime={submission.soa_prime}
          soaAnswersV2={submission.soa_answers_v2}
        />
      </StepDetailModal>

      <StepDetailModal 
        isOpen={activeModal === 'step9'} 
        onClose={() => setActiveModal(null)}
        stepNumber="9"
        stepTitle="Script Selection"
      >
        <Step9Content selectedScript={selectedScript} scriptVariants={scriptVariants} />
      </StepDetailModal>

      <StepDetailModal 
        isOpen={activeModal === 'step10'} 
        onClose={() => setActiveModal(null)}
        stepNumber="10"
        stepTitle="Storyboard"
      >
        <Step10Content submissionScores={submissionScores} storyboardBeats={storyboardBeats} />
      </StepDetailModal>
    </div>
  );
};

export default QueueReview;
